import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { fetchJobs, updateJob } from "../services/api";
import ScoreBadge from "../components/ScoreBadge";

const COLUMNS = [
  { id: "new", title: "New", color: "border-blue-400" },
  { id: "applied", title: "Applied", color: "border-yellow-400" },
  { id: "interview", title: "Interview", color: "border-purple-400" },
  { id: "offer", title: "Offer", color: "border-green-400" },
  { id: "rejected", title: "Rejected", color: "border-red-400" },
];

export default function KanbanBoard() {
  const queryClient = useQueryClient();

  const { data: jobs = [], isLoading } = useQuery({
    queryKey: ["jobs"],
    queryFn: fetchJobs,
  });

  const moveMutation = useMutation({
    mutationFn: ({ jobId, status }) => updateJob(jobId, { status }),
    onMutate: async ({ jobId, status }) => {
      await queryClient.cancelQueries({ queryKey: ["jobs"] });
      const prev = queryClient.getQueryData(["jobs"]);
      queryClient.setQueryData(["jobs"], (old) =>
        old.map((j) =>
          j.jobId === jobId
            ? {
                ...j,
                status,
                dateApplied:
                  status === "applied" && !j.dateApplied
                    ? new Date().toISOString()
                    : j.dateApplied,
              }
            : j
        )
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      queryClient.setQueryData(["jobs"], ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
  });

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const newStatus = result.destination.droppableId;
    const jobId = result.draggableId;
    const job = jobs.find((j) => j.jobId === jobId);
    if (job && job.status !== newStatus) {
      moveMutation.mutate({ jobId, status: newStatus });
    }
  };

  const grouped = COLUMNS.reduce((acc, col) => {
    acc[col.id] = jobs
      .filter((j) => j.status === col.id)
      .sort((a, b) => b.score - a.score);
    return acc;
  }, {});

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-pulse text-gray-400 text-lg">
          Loading board...
        </div>
      </div>
    );
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="flex gap-4 overflow-x-auto pb-4">
        {COLUMNS.map((col) => (
          <div
            key={col.id}
            className={`flex-shrink-0 w-72 bg-gray-50 rounded-xl border-t-4 ${col.color}`}
          >
            <div className="p-3 flex items-center justify-between">
              <h3 className="font-semibold text-gray-700">{col.title}</h3>
              <span className="text-xs bg-gray-200 text-gray-600 rounded-full px-2 py-0.5">
                {grouped[col.id]?.length || 0}
              </span>
            </div>

            <Droppable droppableId={col.id}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`kanban-column p-2 min-h-[200px] space-y-2 transition-colors ${
                    snapshot.isDraggingOver ? "bg-blue-50" : ""
                  }`}
                  style={{ maxHeight: "calc(100vh - 320px)", overflowY: "auto" }}
                >
                  {grouped[col.id]?.map((job, idx) => (
                    <Draggable
                      key={job.jobId}
                      draggableId={job.jobId}
                      index={idx}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`bg-white rounded-lg shadow-sm border p-3 cursor-grab active:cursor-grabbing ${
                            snapshot.isDragging
                              ? "shadow-lg ring-2 ring-brand-500"
                              : "border-gray-100"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h4 className="text-sm font-semibold text-gray-800 leading-tight">
                              {job.title}
                            </h4>
                            <ScoreBadge score={job.score} />
                          </div>
                          <p className="text-xs text-gray-500">{job.company}</p>
                          {job.dateApplied && (
                            <p className="text-xs text-gray-400 mt-1">
                              Applied{" "}
                              {new Date(job.dateApplied).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        ))}
      </div>
    </DragDropContext>
  );
}
